import { useEffect, useState, useCallback } from 'react';
import { apiRequest } from '../utils/auth';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './AnnouncementsPage.css';
import Pagination from '../components/Pagination';

interface Announcement {
  _id: string;
  title: string;
  content: string;
  startTime: string;
  endTime: string;
  createdAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export default function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest<{
        success: boolean;
        data: Announcement[];
        pagination: PaginationInfo;
      }>(`/api/admin/announcements?page=${currentPage}&limit=20`);

      if (response.success) {
        setAnnouncements(response.data);
        setPagination(response.pagination);
      }
    } catch (err) {
      setError('Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const openCreateModal = () => {
    setEditingAnnouncement(null);
    setTitle('');
    setContent('');
    setStartTime(null);
    setEndTime(null);
    setShowModal(true);
  };

  const openEditModal = (a: Announcement) => {
    setEditingAnnouncement(a);
    setTitle(a.title);
    setContent(a.content);
    setStartTime(new Date(a.startTime));
    setEndTime(new Date(a.endTime));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAnnouncement(null);
  };

  // Escape dismisses the form, same as the backdrop click.
  useEscapeToClose(showModal, closeModal);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !content || !startTime || !endTime) return;

    setSubmitting(true);
    try {
      const body = {
        title,
        content,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
      };

      if (editingAnnouncement) {
        await apiRequest(`/api/admin/announcements/${editingAnnouncement._id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
      } else {
        await apiRequest('/api/admin/announcements', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }

      closeModal();
      fetchAnnouncements();
    } catch (err) {
      alert('Failed to save announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await apiRequest(`/api/admin/announcements/${id}`, { method: 'DELETE' });
      fetchAnnouncements();
    } catch (err) {
      alert('Failed to delete announcement');
    }
  };

  const isActive = (a: Announcement) => {
    const now = new Date();
    return new Date(a.startTime) <= now && new Date(a.endTime) >= now;
  };

  if (loading) {
    return <div className="page announcements-page"><div className="loading">Loading announcements...</div></div>;
  }

  if (error) {
    return <div className="page announcements-page"><div className="error">{error}</div></div>;
  }

  return (
    <div className="page announcements-page">
      <div className="page-header">
        <h1>Announcements</h1>
        <button className="create-btn" onClick={openCreateModal}>
          <FiPlus /> Create Announcement
        </button>
      </div>

      <div className="announcements-legacy-banner">
        <strong>Announcements are now popups.</strong> App builds with popups no longer read this page — they show
        alert popups instead. Use <Link to="/popups">Popups → Import announcements</Link> to bring the
        announcements below across (safe to repeat), and make new ones there as an Alert popup triggered at App
        launch. This page still reaches older app versions, and is removed once they're no longer supported.
        Editing an announcement here doesn't change a popup already imported from it.
      </div>

      <table className="announcements-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Content</th>
            <th>Start Time</th>
            <th>End Time</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {announcements.length === 0 ? (
            <tr><td colSpan={6} className="empty-state">No announcements yet.</td></tr>
          ) : (
            announcements.map((a) => (
              <tr key={a._id}>
                <td data-label="Title">{a.title}</td>
                <td data-label="Content" className="content-cell">{a.content.length > 80 ? a.content.slice(0, 80) + '...' : a.content}</td>
                <td data-label="Start Time">{new Date(a.startTime).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}</td>
                <td data-label="End Time">{new Date(a.endTime).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}</td>
                <td data-label="Status">
                  <span className={`status-badge ${isActive(a) ? 'active' : 'inactive'}`}>
                    {isActive(a) ? 'Active' : new Date(a.endTime) < new Date() ? 'Expired' : 'Scheduled'}
                  </span>
                </td>
                <td data-label="Actions">
                  <button className="edit-btn" onClick={() => openEditModal(a)}><FiEdit2 /></button>
                  <button className="delete-btn" onClick={() => handleDelete(a._id)}><FiTrash2 /></button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {pagination && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.pages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title</label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={4} required />
              </div>
              <div className="form-group">
                <label>Start Time</label>
                <DatePicker
                  selected={startTime}
                  onChange={(date: Date | null) => setStartTime(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="yyyy-MM-dd HH:mm"
                  timeCaption="Time"
                  placeholderText="Select start time"
                  className="datepicker-input"
                  required
                />
              </div>
              <div className="form-group">
                <label>End Time</label>
                <DatePicker
                  selected={endTime}
                  onChange={(date: Date | null) => setEndTime(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="yyyy-MM-dd HH:mm"
                  timeCaption="Time"
                  placeholderText="Select end time"
                  minDate={startTime || undefined}
                  className="datepicker-input"
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="submit-btn" disabled={submitting}>
                  {submitting ? 'Saving...' : editingAnnouncement ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
